import { useState } from "react";
import { Link as LinkIcon, X } from "@phosphor-icons/react";
import { formatCurrency } from "shared/utils/currency";
import { LinkedTicket, TicketSuggestion } from "../shared/opexInvoiceCells";
import { getLabelIconAndColor } from "../../shared/helpdeskCellFactory";
import { TicketLabel } from "shared/types/helpdesk";

/**
 * Kostenplaats ingeklapt tot "gebouw · N units", uitklapbaar.
 *
 * 58% van de factuurregels heeft meer dan één kostenplaats (tot 63), maar 99,5%
 * daarvan zit in hetzelfde gebouw — de korte vorm is dus volledig én kort. De
 * volledige opsomming maakte rijen zes keer zo hoog. Percentages zijn weggelaten:
 * elke analytische lijn is 100% van zichzelf, dus dat zei niets.
 *
 * Bij precies 1 kostenplaats gaat het altijd over één specifieke unit, niet het
 * hele gebouw — dan staat meteen de volledige unit-id er (voorheen enkel de
 * gebouwnaam, met de unit-id pas zichtbaar via hover).
 */
export const OpexKostenplaatsCell = ({
  kostenplaatsen = [],
  buildings = [],
}: {
  kostenplaatsen?: Array<{ id: number; name: string; amount: number }>;
  buildings?: string[];
}) => {
  const [expanded, setExpanded] = useState(false);

  if (kostenplaatsen.length === 0) {
    return <span className="text-neutral-secondary-mid text-xs">—</span>;
  }

  const label =
    kostenplaatsen.length === 1
      ? kostenplaatsen[0].name
      : buildings.length === 1
        ? buildings[0]
        : buildings.length > 1
          ? `${buildings.length} gebouwen`
          : kostenplaatsen[0].name;

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setExpanded((open) => !open);
        }}
        title={kostenplaatsen.map((k) => k.name).join(", ")}
        className="text-neutral-primary hover:text-moss-primary block max-w-full truncate text-left text-sm"
      >
        {label}
        {kostenplaatsen.length > 1 && (
          <span className="text-neutral-secondary-mid"> · {kostenplaatsen.length} units</span>
        )}
      </button>

      {expanded && (
        <ul className="text-neutral-secondary-mid mt-1 space-y-0.5 text-xs">
          {kostenplaatsen.map((kostenplaats) => (
            <li key={kostenplaats.id} className="flex justify-between gap-2">
              <span className="truncate">{kostenplaats.name}</span>
              <span className="shrink-0">{formatCurrency(kostenplaats.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Labeldeel van een ticketchip (bv. "Sanitair"), met dezelfde icoon+kleur als in
 * de helpdesklijst (`getLabelIconAndColor`). Zonder label — of bij `Onbekend` —
 * niets: dat is geen fout, alleen niets te melden.
 */
const TicketLabelBadge = ({ label }: { label?: string | null }) => {
  if (!label || label === TicketLabel.UNKNOWN) return null;

  const { icon, bgColor, textColor } = getLabelIconAndColor(label as TicketLabel);
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-0.5 rounded px-1 ${bgColor} ${textColor}`}
    >
      {icon}
      {label}
    </span>
  );
};

/**
 * Ticketkolom: het gekoppelde ticket, of kandidaten die met één klik gekoppeld
 * worden.
 *
 * 16% van de factuurregels noemt zelf een ticketnummer, en de omschrijving is vaak
 * genoeg om de rest te herkennen — daarom staan de voorstellen hier in de rij en
 * niet achter een modal.
 *
 * Het label van het ticket (bv. "Sanitair") staat in dezelfde chip als het ticket
 * zelf — ID, dan label, dan titel — zowel bij een gekoppeld ticket als bij een
 * voorstel. Er was hiervoor een aparte kolom, maar die stond los van het ticket
 * dat ze beschreef.
 */
export const OpexTicketCell = ({
  linkedTickets,
  suggestions = [],
  onPickTicket,
  onAcceptSuggestion,
  onUnlink,
}: {
  linkedTickets: LinkedTicket[];
  suggestions?: TicketSuggestion[];
  onPickTicket: () => void;
  onAcceptSuggestion: (ticketId: number) => void;
  onUnlink: (invoiceLineDbId: number) => void;
}) => {
  if (linkedTickets.length > 0) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        {linkedTickets.map((ticket) => {
          const hasLabel = !!ticket.label && ticket.label !== TicketLabel.UNKNOWN;

          return (
            <span
              key={ticket.id}
              className="bg-moss-secondary-light text-neutral-primary inline-flex max-w-full items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
              title={`H${ticket.id}${hasLabel ? ` ${ticket.label}` : ""} ${ticket.title}`}
            >
              <LinkIcon size={12} className="text-moss-primary shrink-0" />
              <span className="text-moss-primary shrink-0 font-semibold">
                H{ticket.id}
              </span>
              <TicketLabelBadge label={ticket.label} />
              <span className="truncate">{ticket.title}</span>
              {ticket.invoiceLineDbId && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onUnlink(ticket.invoiceLineDbId!);
                  }}
                  aria-label={`Ontkoppel ticket H${ticket.id}`}
                  title="Ontkoppelen"
                  className="hover:text-brick-primary shrink-0"
                >
                  <X size={12} />
                </button>
              )}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.ticketId}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onAcceptSuggestion(suggestion.ticketId);
          }}
          title={suggestion.reasons.join(" · ")}
          className={`inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
            suggestion.isExplicitReference
              ? "border-moss-primary text-neutral-primary bg-moss-secondary-light font-medium"
              : "border-neutral-primary/20 text-neutral-primary hover:border-moss-primary"
          }`}
        >
          <span className="text-moss-primary shrink-0 font-semibold">
            H{suggestion.ticketId}
          </span>
          <TicketLabelBadge label={suggestion.label} />
          <span className="truncate">{suggestion.title}</span>
        </button>
      ))}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onPickTicket();
        }}
        className="text-moss-primary hover:bg-moss-primary/10 rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap"
      >
        {suggestions.length > 0 ? "ander ticket…" : "Koppel ticket"}
      </button>
    </div>
  );
};
